import React, { useState } from 'react';
import { Alert, Box, LinearProgress, Link, Stack, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { contractHref } from '../../../../common/constants/routes.constants';
import { useTranslation } from 'react-i18next';
import GenericButton, { GenericButtonTypeEnum } from '../../../../components/GenericButton';
import RowActionsMenu from '../../../../components/RowActionsMenu/RowActionsMenu';
import StatusChip from '../../../../components/StatusChip';
import { MESSAGE_TYPE } from '../../../../common/constants/common.constants';
import {
  DiscountModeEnum,
  INVOICE_LINE_TYPE_LABELS,
  InvoiceDeliveryStatusEnum,
  InvoiceLineTypeEnum,
  InvoicePdfStatusEnum,
  InvoiceRecipientStatusEnum,
  InvoiceStatusEnum,
  PAYMENT_METHOD_LABELS
} from '../../../../common/enums/invoicing.enum';
import { InvoiceDetailsDto } from '../../../../common/interfaces/invoicing.interface';
import { getInvoicePdfUrl } from '../../../../api/invoicing.api';
import { createNotification } from '../../../../utilities/helper/notification.helper';
import { downloadFromPresignedUrl } from '../../../../utilities/helper/download.helper';
import { formatMoney, formatQuantity } from '../../../../utilities/helper/money.helper';
import { daysBetween, formatMonthKey, formatSerbianDate, toLocalDateString } from '../../../../utilities/helper/date.helper';
import { invoiceStateOf } from '../../../../utilities/helper/invoiceState.helper';
import PaymentPanel from '../Payments/PaymentPanel';
import SendInvoicesDialog from './SendInvoicesDialog';

interface InvoiceDetailViewProps {
  kindergartenId: string;
  invoice: InvoiceDetailsDto;
  onChanged: () => void;
  onCancel: () => void;
}

const Card: React.FC<{ title: string; children: React.ReactNode; aside?: React.ReactNode }> = ({ title, children, aside }) => (
  <Box component="section" aria-label={title} sx={{ border: 1, borderColor: 'line', borderRadius: 4, p: 3 }}>
    <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mb: 2 }}>
      <Typography component="h2" sx={{ fontSize: 16, fontWeight: 600 }}>
        {title}
      </Typography>
      {aside}
    </Stack>
    {children}
  </Box>
);

const Row: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <Box sx={{ display: 'grid', gridTemplateColumns: '160px 1fr', gap: 2, py: 0.75 }}>
    <Typography sx={{ fontSize: 14, color: 'text.secondary' }}>{label}</Typography>
    <Box sx={{ fontSize: 14 }}>{children}</Box>
  </Box>
);

/**
 * An issued (or cancelled) invoice: what it bills, where the money goes, and how far it
 * has been paid.
 *
 * The payment card is on the right because it is the question an open invoice is opened
 * for. "Record a payment" records it for the child, not for this invoice - the note under
 * the card says so - and the panel shows, before saving, which invoices it will close.
 */
const InvoiceDetailView: React.FC<InvoiceDetailViewProps> = ({ kindergartenId, invoice, onChanged, onCancel }) => {
  const { t } = useTranslation();
  const [paying, setPaying] = useState(false);
  const [sending, setSending] = useState(false);
  const today = toLocalDateString(new Date());
  const remaining = Math.max(invoice.totalGross - invoice.paidAmount, 0);
  const issued = invoice.status === InvoiceStatusEnum.ISSUED;
  const cancelled = invoice.status === InvoiceStatusEnum.CANCELLED;
  const lateBy = issued && remaining > 0 && invoice.dueDate && invoice.dueDate < today ? daysBetween(invoice.dueDate, today) : 0;
  const month = formatMonthKey(invoice.periodStart.slice(0, 7));
  const discountTotal = -invoice.lines
    .filter(line => line.lineType === InvoiceLineTypeEnum.DISCOUNT)
    .reduce((sum, line) => sum + line.lineGross, 0);
  // an invoice for nothing is paid by being issued
  const nothingToPay = issued && invoice.totalGross <= 0;
  const paidShare = nothingToPay ? 100 : invoice.totalGross > 0 ? Math.min(100, (invoice.paidAmount / invoice.totalGross) * 100) : 0;

  const download = async () => {
    try {
      const result = await getInvoicePdfUrl(kindergartenId, invoice.id);
      if (result.results) await downloadFromPresignedUrl(result.results.url, result.results.fileName);
    } catch (error: any) {
      createNotification(MESSAGE_TYPE.ERROR, error?.response?.data?.message ?? t('invoices.pdf.downloadFailed'));
    }
  };

  const subline = [
    invoice.childFullName,
    invoice.groupName,
    month,
    invoice.issueDate && t('invoices.detail.issuedOn', { date: formatSerbianDate(invoice.issueDate) }),
    invoice.deliveryStatus === InvoiceDeliveryStatusEnum.SENT &&
      invoice.sentToPayerAt &&
      t('invoices.detail.sentTo', {
        name: invoice.payerSnapshot?.fullName ?? invoice.payerFullName,
        date: formatSerbianDate(invoice.sentToPayerAt)
      })
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        justifyContent="space-between"
        alignItems={{ md: 'flex-start' }}
        spacing={2}
        sx={{ mb: 3 }}
      >
        <Box>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Typography variant="h1">{t('invoices.detail.title', { number: invoice.invoiceNumber ?? '—' })}</Typography>
            <StatusChip kind="invoiceState" status={invoiceStateOf(invoice)} />
          </Stack>
          <Typography sx={{ fontSize: 14, color: 'text.secondary', mt: 0.75 }}>{subline}</Typography>
          {issued && invoice.pdfStatus !== InvoicePdfStatusEnum.READY && (
            <Typography
              sx={{ fontSize: 12, color: invoice.pdfStatus === InvoicePdfStatusEnum.FAILED ? 'error.main' : 'text.secondary', mt: 0.5 }}
            >
              {invoice.pdfStatus === InvoicePdfStatusEnum.FAILED ? t('invoices.pdf.failed') : t('invoices.pdf.preparing')}
            </Typography>
          )}
        </Box>
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flexShrink: 0 }}>
          {invoice.hasPdf && (
            <GenericButton
              buttonType={GenericButtonTypeEnum.Secondary}
              variant="outlined"
              onAsyncAction={download}
              label={t('invoices.pdf.download')}
            />
          )}
          {issued && remaining > 0 && (
            <GenericButton
              buttonType={GenericButtonTypeEnum.Primary}
              variant="contained"
              onClick={() => setPaying(true)}
              label={t('payments.panel.open')}
            />
          )}
          {issued && (
            <RowActionsMenu
              name={invoice.invoiceNumber ?? invoice.childFullName}
              actions={[
                {
                  label: invoice.deliveryStatus === InvoiceDeliveryStatusEnum.SENT ? t('invoices.send.again') : t('invoices.send.toPayer'),
                  disabled: !invoice.payerEmail,
                  onClick: () => setSending(true)
                },
                { label: t('invoices.cancel.title'), danger: true, onClick: onCancel }
              ]}
            />
          )}
        </Stack>
      </Stack>

      {!!invoice.warnings?.length && (
        <Alert severity="warning" sx={{ mb: 3, borderRadius: 2 }}>
          {invoice.warnings.join(' ')}
        </Alert>
      )}

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 400px' }, gap: 3, alignItems: 'start' }}>
        <Stack spacing={3}>
          <Card title={t('invoices.page.lines')}>
            <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0 }}>
              {invoice.lines.map(line =>
                line.lineType === InvoiceLineTypeEnum.DISCOUNT ? (
                  <Box
                    component="li"
                    key={line.id}
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      gap: 2,
                      py: 1,
                      ml: '28px',
                      borderTop: 1,
                      borderTopStyle: 'dashed',
                      borderColor: 'line'
                    }}
                  >
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ fontSize: 14 }}>{line.description}</Typography>
                      <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                        {t('discounts.detailSub', {
                          value:
                            line.discountMode === DiscountModeEnum.PERCENT ? `${line.discountValue} %` : formatMoney(line.discountValue),
                          target: line.appliesToDescription ?? '—'
                        })}
                      </Typography>
                    </Box>
                    <Typography
                      sx={{
                        fontSize: 14,
                        fontWeight: 500,
                        color: 'success.main',
                        minWidth: 96,
                        textAlign: 'right',
                        fontVariantNumeric: 'tabular-nums',
                        flexShrink: 0
                      }}
                    >
                      −{formatMoney(Math.abs(line.lineGross))}
                    </Typography>
                  </Box>
                ) : (
                  <Box
                    component="li"
                    key={line.id}
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      gap: 2,
                      py: 1.25,
                      '& + &': { borderTop: 1, borderColor: 'line' }
                    }}
                  >
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ fontSize: 14, fontWeight: 500 }}>{line.description}</Typography>
                      <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                        {t(INVOICE_LINE_TYPE_LABELS[line.lineType])}
                        {line.lineType === InvoiceLineTypeEnum.ATTENDANCE_BASED && line.attendanceDays !== null
                          ? ` · ${t('invoices.detail.attendanceDays', { count: line.attendanceDays })}`
                          : ''}
                      </Typography>
                    </Box>
                    <Stack direction="row" spacing={3} sx={{ flexShrink: 0, fontVariantNumeric: 'tabular-nums' }}>
                      <Typography sx={{ fontSize: 14, color: 'text.secondary' }}>
                        {formatQuantity(line.quantity)} × {formatMoney(line.unitPriceNet)}
                      </Typography>
                      <Typography sx={{ fontSize: 14, fontWeight: 500, minWidth: 96, textAlign: 'right' }}>
                        {formatMoney(line.lineGross)}
                      </Typography>
                    </Stack>
                  </Box>
                )
              )}
            </Box>
            <Stack direction="row" justifyContent="space-between" sx={{ borderTop: 1, borderColor: 'line', pt: 2, mt: 1 }}>
              <Typography sx={{ fontSize: 14, fontWeight: 600 }}>{t('invoices.columns.total')}</Typography>
              <Typography sx={{ fontSize: 16, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                {formatMoney(invoice.totalGross, { withCurrency: true, currency: invoice.currency })}
              </Typography>
            </Stack>
            {discountTotal > 0 && (
              <Typography sx={{ fontSize: 12, color: 'text.secondary', textAlign: 'right', mt: 0.5 }}>
                {t('discounts.ofWhich', { amount: formatMoney(discountTotal) })}
              </Typography>
            )}
          </Card>

          <Card title={t('invoices.detail.paymentDetails')}>
            <Row label={t('contracts.payer')}>
              {invoice.payerSnapshot?.fullName ?? invoice.payerFullName}
              {invoice.payerEmail && (
                <Typography component="span" sx={{ fontSize: 13, color: 'text.secondary', display: 'block' }}>
                  {invoice.payerEmail}
                </Typography>
              )}
            </Row>
            <Row label={t('contracts.title')}>
              {invoice.contractNumber ? (
                <Link component={RouterLink} to={contractHref(kindergartenId, invoice.childId)} underline="hover">
                  {invoice.contractNumber}
                </Link>
              ) : (
                '—'
              )}
            </Row>
            <Row label={t('invoices.detail.reference')}>
              {invoice.paymentReference ? `${invoice.paymentReferenceModel} ${invoice.paymentReference}` : '—'}
            </Row>
            <Row label={t('invoices.page.notePrinted')}>{invoice.note || '—'}</Row>
          </Card>

          {!!invoice.deliveries?.length && (
            <Card title={t('invoices.recipients.title')}>
              <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0 }}>
                {invoice.deliveries.map(delivery => (
                  <Box
                    component="li"
                    key={delivery.email}
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      gap: 2,
                      py: 1.25,
                      '& + &': { borderTop: 1, borderColor: 'line' }
                    }}
                  >
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ fontSize: 14, fontWeight: 500 }}>
                        {delivery.fullName}
                        <Box component="span" sx={{ fontSize: 12, fontWeight: 400, color: 'text.secondary' }}>
                          {' · '}
                          {delivery.isPayer ? t('invoices.recipients.payer') : t('invoices.recipients.copy')}
                        </Box>
                      </Typography>
                      <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{delivery.email}</Typography>
                      {delivery.status === InvoiceRecipientStatusEnum.FAILED && delivery.error && (
                        <Typography sx={{ fontSize: 12, color: 'error.main' }}>{delivery.error}</Typography>
                      )}
                    </Box>
                    <Stack alignItems="flex-end" spacing={0.5} sx={{ flexShrink: 0 }}>
                      <StatusChip kind="recipient" status={delivery.status} />
                      <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                        {delivery.sentAt
                          ? t('invoices.send.sentOn', { date: formatSerbianDate(delivery.sentAt) })
                          : t('invoices.recipients.requestedOn', { date: formatSerbianDate(delivery.requestedAt) })}
                      </Typography>
                    </Stack>
                  </Box>
                ))}
              </Box>
            </Card>
          )}
        </Stack>

        {cancelled ? (
          <Card title={t('invoices.detail.cancelled')}>
            <Typography sx={{ fontSize: 14 }}>{invoice.cancelReason || '—'}</Typography>
          </Card>
        ) : (
          <Card title={t('invoices.detail.payment')}>
            <Row label={t('invoices.detail.amount')}>{formatMoney(invoice.totalGross, { withCurrency: true })}</Row>
            <Row label={t('finance.figures.paid')}>{formatMoney(invoice.paidAmount, { withCurrency: true })}</Row>
            <Box sx={{ display: 'grid', gridTemplateColumns: '160px 1fr', gap: 2, alignItems: 'baseline', py: 0.75 }}>
              <Typography sx={{ fontSize: 14, color: 'text.secondary' }}>{t('invoices.columns.remaining')}</Typography>
              <Typography
                sx={{
                  fontSize: 20,
                  fontWeight: 600,
                  color: remaining > 0 ? 'error.main' : 'success.main',
                  fontVariantNumeric: 'tabular-nums'
                }}
              >
                {formatMoney(remaining, { withCurrency: true })}
              </Typography>
            </Box>
            {nothingToPay && (
              <Typography sx={{ fontSize: 14, color: 'success.main', fontWeight: 500 }}>{t('invoices.detail.paidNothingToPay')}</Typography>
            )}
            <LinearProgress
              variant="determinate"
              value={paidShare}
              aria-label={t('invoices.detail.paidShare')}
              sx={{
                height: 8,
                borderRadius: 999,
                my: 1.5,
                bgcolor: 'line',
                '& .MuiLinearProgress-bar': { bgcolor: 'success.main', borderRadius: 999 }
              }}
            />
            <Row label={t('invoices.columns.dueDate')}>
              <Box component="span" sx={lateBy > 0 ? { color: 'error.main' } : undefined}>
                {formatSerbianDate(invoice.dueDate)}
                {lateBy > 0 && ` · ${t('invoices.list.lateBy', { count: lateBy })}`}
              </Box>
            </Row>

            <Typography component="h3" sx={{ fontSize: 14, fontWeight: 600, mt: 2, mb: 1 }}>
              {t('invoices.detail.paymentsOnThis')}
            </Typography>
            {invoice.payments?.length ? (
              <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0 }}>
                {invoice.payments.map(payment => (
                  <Box component="li" key={payment.paymentId} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.75 }}>
                    <Box>
                      <Typography sx={{ fontSize: 14 }}>{formatSerbianDate(payment.paidAt)}</Typography>
                      <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                        {[t(PAYMENT_METHOD_LABELS[payment.method]), payment.paymentReference].filter(Boolean).join(' · ')}
                      </Typography>
                    </Box>
                    <Typography sx={{ fontSize: 14, fontWeight: 500, fontVariantNumeric: 'tabular-nums' }}>
                      {formatMoney(payment.amount)}
                    </Typography>
                  </Box>
                ))}
              </Box>
            ) : (
              <Typography sx={{ fontSize: 14, color: 'text.secondary' }}>{t('invoices.detail.noPayments')}</Typography>
            )}
            <Typography sx={{ fontSize: 12, color: 'text.secondary', mt: 2 }}>{t('invoices.detail.paymentNote')}</Typography>
          </Card>
        )}
      </Box>

      {paying && (
        <PaymentPanel
          kindergartenId={kindergartenId}
          childId={invoice.childId}
          childName={invoice.childFullName}
          payerName={invoice.payerSnapshot?.fullName ?? invoice.payerFullName}
          invoice={{
            invoiceNumber: invoice.invoiceNumber,
            remaining,
            paymentReference: invoice.paymentReference ? `${invoice.paymentReferenceModel} ${invoice.paymentReference}` : null
          }}
          onClose={() => setPaying(false)}
          onRecorded={onChanged}
        />
      )}
      {sending && (
        <SendInvoicesDialog kindergartenId={kindergartenId} invoices={[invoice]} onClose={() => setSending(false)} onSent={onChanged} />
      )}
    </>
  );
};

export default InvoiceDetailView;
